import axios, { AxiosError } from 'axios'
import { notification } from '../components/Notifications'
import { translateUserFacingText } from './userFacingTerminology'

/**
 * ============================================================================
 * Error Notification & Resolution System
 * ============================================================================
 *
 * Goal:
 * Convert ANY thrown value (Axios, fetch, SDK, wrapped errors, strings)
 * into a deterministic, user-friendly notification message.
 *
 * Design principles:
 * - Shallow-first resolution (avoid accidental deep matches)
 * - Explicit overrides when needed
 * - Safe defaults
 * - Scalable resolver pipeline
 */

/* ============================================================================
 * Message Extraction
 * ========================================================================== */

/**
 * Maximum allowed recursion depth during extraction.
 * Prevents infinite loops and pathological payloads.
 */
const MAX_EXTRACTION_DEPTH = 3

/**
 * Extracts a human-readable message from unknown values.
 *
 * Rules:
 * - Strings are trusted
 * - Arrays are flattened and joined
 * - Objects are scanned ONLY at top level
 * - No implicit deep traversal
 */

/* ============================================================================
 * Usage Examples (for end users)
 * ========================================================================== */

// Example 1: Simple error handling (Axios/fetch/JS error)
// try {
//   await axios.get('/api/data')
// } catch (error) {
//   notifyError(error)
// }

// Example 2: Custom error message
// notifyError('Failed to upload file. Please try again.')

// Example 3: Explicit key extraction
// try {
//   // Some operation that returns an error object with a specific key
// } catch (error) {
//   notifyError(error, { messageKey: 'detail' })
// }

// Example 4: Multiple keys, custom variant, fallback message
// try {
//   // Some API call or operation
// } catch (error) {
//   notifyError(error, {
//     messageKey: ['detail', 'description', 'error'],
//     variant: 'warning',
//     fallbackMessage: 'Unable to process your request. Please contact support.',
//     autoHideDuration: 6000,
//   })
// }

// Example 5: Fallback message only
// try {
//   // Some operation
// } catch (error) {
//   notifyError(error, { fallbackMessage: 'Something went wrong. Contact support.' })
// }

const extractReadableMessage = (
  value: unknown,
  depth = 0
): string | null => {
  if (value == null || depth > MAX_EXTRACTION_DEPTH) return null

  // Plain string
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length ? trimmed : null
  }

  // Array → join extracted messages
  if (Array.isArray(value)) {
    const messages = value
      .map(v => extractReadableMessage(v, depth + 1))
      .filter(Boolean) as string[]

    return messages.length ? messages.join(', ') : null
  }

  // Object → strict shallow scan
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>

    /**
     * Priority order matters.
     * We NEVER traverse nested objects implicitly.
     */
    const PRIORITY_KEYS = [
      'error',
      'message',
      'title',
      'detail',
      'description'
    ]

    for (const key of PRIORITY_KEYS) {
      if (key in record) {
        const message = extractReadableMessage(
          record[key],
          depth + 1
        )
        if (message) return message
      }
    }

    // Generic validation payloads may use dynamic field keys.
    // Fall back to the first readable field value (for example: end_timestamp).
    for (const [, fieldValue] of Object.entries(record)) {
      const message = extractReadableMessage(fieldValue, depth + 1)
      if (message) return message
    }

    return null
  }

  return null
}

/* ============================================================================
 * Helpers
 * ========================================================================== */

/**
 * Safely resolves a value from an object using dot-notation.
 *
 * Example:
 *   getValueAtPath(obj, "message.error")
 */
const getValueAtPath = (
  source: unknown,
  path: string
): unknown => {
  if (!source || typeof source !== 'object') return null

  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object') {
      return (acc as any)[key]
    }
    return null
  }, source)
}

/**
 * Unwraps common SDK / library error wrappers.
 *
 * Example:
 *   { error: {...realError} } → realError
 */
const unwrapError = (error: unknown): unknown => {
  if (
    error &&
    typeof error === 'object' &&
    'error' in (error as any)
  ) {
    return (error as any).error
  }

  return error
}

/**
 * Friendly UX messages for known HTTP status codes.
 */
const STATUS_FALLBACK_MESSAGES: Record<number, string> = {
  401: 'You are not authorized.',
  403: 'You do not have permission.',
  404: 'Requested resource not found.'
}

const getHttpStatus = (error: unknown): number | undefined =>
  (error as any)?.response?.status ?? (error as any)?.status

/* ============================================================================
 * Resolver Types
 * ========================================================================== */

type ErrorResolver = (error: unknown) => string | null

/**
 * Explicit key-based resolver (HIGHEST priority).
 *
 * Used when the caller knows exactly where the message lives.
 */
const resolveMessageByExplicitKey =
  (messageKey?: string | string[]): ErrorResolver =>
    inputError => {
      if (!messageKey) return null

      const error = unwrapError(inputError)

      const keys = Array.isArray(messageKey)
        ? messageKey
        : [messageKey]

      for (const key of keys) {
      /**
       * Ergonomic shorthand:
       * "error" → "message.error"
       */
        const candidatePaths = [
          key,
        `message.${key}`
        ]

        for (const path of candidatePaths) {
          /**
           * `data` is checked alongside `response.data`: the shared client's interceptor rejects with
           * `{ message, data, status }` and no `response` at all, so a caller naming a field key
           * — a 422's `endTimestamp`, say — used to miss it here and fall through to the automatic
           * pipeline, whose last resort is the first readable value in the body. On a payload that
           * also carries its boundary (`latestAllowedEndTimestamp`) that could toast a bare ISO
           * timestamp in place of the sentence the caller asked for. Found by review on #1936.
           */
          const value =
          getValueAtPath((error as any)?.response?.data, path) ??
          getValueAtPath((error as any)?.data, path) ??
          getValueAtPath(error, path)

          const message = extractReadableMessage(value)
          if (message) return message
        }
      }

      return null
    }

/**
 * Frontend-thrown errors (error.message)
 */
const resolveNativeErrorMessage: ErrorResolver = error =>
  extractReadableMessage((error as any)?.message)

/**
 * Interceptor-structured errors place the raw response body on a top-level
 * `data` key (not nested under `response`). Checked before `message` so that
 * field-level 422 payloads (Shape 2: { field_key: "msg" }) surface their actual
 * validation text rather than the interceptor's generic fallback string.
 */
const resolveInterceptorData: ErrorResolver = error =>
  extractReadableMessage((error as any)?.data)

/**
 * Axios response payload
 */
const resolveAxiosResponseData: ErrorResolver = error =>
  extractReadableMessage((error as any)?.response?.data)

/**
 * Status-based fallbacks
 */
const resolveStatusFallback: ErrorResolver = error => {
  const status = getHttpStatus(error)

  if (!status || status === 422) return null

  return (
    STATUS_FALLBACK_MESSAGES[status] ??
    (status >= 500
      ? 'A server error occurred. Please try again later.'
      : null)
  )
}

/**
 * Axios transport-level failures — only applies to real AxiosError instances,
 * not plain objects that happen to carry isAxiosError: true (e.g. interceptor-
 * structured errors re-thrown by createApiThunk).
 */
const resolveAxiosTransportError: ErrorResolver = error => {
  if (!(error instanceof AxiosError)) return null

  if (error.code === 'ECONNABORTED') {
    return 'Request timed out. Please try again.'
  }

  if (!error.response) {
    return 'Network error. Please check your connection.'
  }

  return null
}

/**
 * Ordered Axios resolution pipeline.
 *
 * Transport errors (timeout, network) must run before resolveNativeErrorMessage
 * so that the friendly message wins over the raw AxiosError.message (e.g. 'timeout').
 */
const axiosErrorResolvers: ErrorResolver[] = [
  resolveInterceptorData,
  resolveAxiosTransportError,
  resolveNativeErrorMessage,
  resolveAxiosResponseData,
  resolveStatusFallback
]

/* ============================================================================
 * Core Resolution API
 * ========================================================================== */

/**
 * Resolves ANY thrown value into a user-facing error message.
 *
 * Public so Pattern A form modals can feed the same extraction into
 * `ValidationAlert` instead of reading the interceptor's generic `message`.
 */
export const resolveErrorMessage = (
  inputError: unknown
): string | null => {
  const error = unwrapError(inputError)

  // Direct string usage
  if (typeof error === 'string') {
    const trimmed = error.trim()
    return trimmed.length ? trimmed : null
  }

  // Axios errors
  if (axios.isAxiosError(error)) {
    // Canceled requests are intentionally silent — no user-facing notification
    if (error instanceof AxiosError && error.code === 'ERR_CANCELED') return null

    for (const resolver of axiosErrorResolvers) {
      const message = resolver(error)
      if (message) return message
    }
    return null
  }

  // Non-Axios but response-shaped errors
  const responseDataMessage = extractReadableMessage(
    (error as any)?.response?.data
  )

  if (responseDataMessage) {
    return responseDataMessage
  }

  // Native JS Error
  if (error instanceof Error) {
    return (
      extractReadableMessage(error.message) ??
      'An unexpected error occurred.'
    )
  }

  return 'An unexpected error occurred.'
}

/* ============================================================================
 * notifyError – Public API
 * ========================================================================== */

export type NotifyErrorOptions =
  Parameters<typeof notification>[1] & {
    /**
     * Used only if no message can be resolved.
     */
    fallbackMessage?: string

    /**
     * Explicit key or dot-path for message extraction.
     * Example: "message.error", "errors.email"
     */
    messageKey?: string | string[]
  }

/**
 * Unified error notification helper.
 *
 * Team rule of thumb:
 * - If you already have copy → pass a string
 * - If you caught something → pass the error
 */
export const notifyError = (
  error: unknown,
  options: NotifyErrorOptions = {}
): void => {
  // Canceled requests are intentionally silent
  if (error instanceof AxiosError && error.code === 'ERR_CANCELED') return

  const {
    fallbackMessage = 'Something went wrong. Please try again.',
    variant = 'error',
    messageKey,
    ...notificationOptions
  } = options

  /**
   * Resolution order:
   * 1. Explicit key (if provided)
   * 2. Automatic resolver pipeline
   * 3. Fallback message
   */
  const message =
    resolveMessageByExplicitKey(messageKey)(error) ??
    resolveErrorMessage(error) ??
    fallbackMessage

  if (!message) return

  notification(translateUserFacingText(message), {
    variant,
    ...notificationOptions
  })
}
